import { useGetAlbumVideo } from '@vimeo/gctl-api/hooks/useAlbumVideo';
import { VideoFields } from '@vimeo/gctl-api/models/components/schemas/video/Video';
import { useViewer } from '@vimeo/viewer';

import {
  DEFAULT_SHOWCASE_QUERY,
  GET_ALBUM_SIGNATURE_PREFIX_KEY,
  GET_ALBUM_VIDEO_HEADER_VERSION,
  VIDEOS_API_VERSION,
} from '../constant';
import { getReferrerValue, getSSGMetaDataFromDOM } from '../helper';
import { useShowcaseAuth } from './use-showcase-auth';

/**
 * Custom React hook to fetch video data for a specific album and clip.
 *
 * @param albumId - The ID of the album to fetch the video from. Can be `null` if not available.
 * @param clipId - The ID of the video (clip) within the album. Can be `null` if not available.
 * @param selectFields - An array of fields to select from the video data.
 * @param notCallable - Optional flag to prevent the hook from making the API call. Defaults to `false`.
 * @returns The result of the `useGetAlbumVideo` hook, which includes video data, loading, and error states.
 *
 * @remarks
 * - The hook handles authentication and album signature retrieval.
 * - If required parameters are missing or `notCallable` is `true`, the API call is skipped.
 * - The hook disables revalidation on window focus for performance reasons.
 */
export const useGetAlbumVideoData = (
  albumId: number | null,
  clipId: number | null,
  selectFields: VideoFields[],
  notCallable: boolean = false,
  customQuery: Record<string, unknown> = {},
) => {
  const viewer = useViewer();
  const albumSignature = getSSGMetaDataFromDOM(`${GET_ALBUM_SIGNATURE_PREFIX_KEY}-${albumId}`);
  const referrer = getReferrerValue();
  const {
    data: showcaseAuthData,
    error: showcaseAuthError,
    isLoading: isShowcaseAuthLoading,
  } = useShowcaseAuth(Number(albumId), { isShowcaseEmbedded: false }, notCallable);
  const password = !showcaseAuthError && !isShowcaseAuthLoading ? showcaseAuthData?.hashedPass : '';

  const embedQuery = {
    referrer,
  };

  return useGetAlbumVideo(
    () => {
      if (notCallable) return null;
      if (!viewer || !albumId || !clipId || albumSignature === null) {
        return null;
      }

      return {
        where: { albumId: albumId, videoId: clipId },
        select: selectFields,
        query: {
          ...DEFAULT_SHOWCASE_QUERY,
          ...embedQuery,
          ...(password && { password }),
          ...(albumSignature && { albumSignature, albumUri: `albums/${albumId}` }),
          ...(customQuery && { ...customQuery }),
        },
        headers: { Accept: `${GET_ALBUM_VIDEO_HEADER_VERSION};version=${VIDEOS_API_VERSION}` },
      };
    },
    {
      revalidateOnFocus: false,
    },
  );
};
